import {
  type Language,
  MARKETPLACE_TO_MARKETPLACE_AMAZON,
  MARKETPLACE_TO_REALM,
  type Marketplace,
  type MarketplaceAmazon,
  marketplaceToConfig,
  type PLATFORMS,
  type SharedVarsMap,
  TrafficCampaignSchema,
} from '@amzn/journeys-common';
import { get, isEmpty, isNil } from 'es-toolkit/compat';
import { isFeatureEnabled } from 'features/flags';
import type { MediaAssetModel } from 'features/traffic/campaigns/content/ContentBlocks/types';
import { generateFlavorCreativeName } from 'features/traffic/campaigns/utils/generate-flavor-creative-name';
import type { SymphonyRealm } from 'generated/graphql';
import { generateDestinationUrlForFlavor } from '../../../content/common-components/RefTaggedLinkEditor/generate-destination-url-for-flavor';
import type { AssetIdToUrlMap } from '../../../content/generic.types';
import { shortenMediaCentralUrl } from '../../../content/generic.util';
import { getSharedOrOwnedValue } from '../../../content/shared-variables/utils';
import {
  AAA_SERVICE_NAME,
  AUTH_PROTOCOL,
  CTI,
  EXPERIMENT_WEBLAB,
  REMOTE_SEARCH_ONTOLOGY_LIFESTYLE,
  REMOTE_SEARCH_ONTOLOGY_LIFESTYLE_ASIN,
  REMOTE_STRATEGY_CONTEXT_KEYS,
  SERVICE_TYPE,
  SERVICE_URL,
  WIDGET_PROCESSING_EXCLUSIONS,
} from '../constants';
import {
  FEATURED_BRANDS_TEMPLATES,
  type FeaturedBrandsContent,
  type FeaturedBrandsFlavor,
  type FeaturedBrandsLifestyleContent,
  type FeaturedBrandsSymphonyBrandsCollectionsContent,
  type FeaturedBrandsSymphonyCommonContent,
  type FeaturedBrandsSymphonyContent,
  type FeaturedBrandsSymphonyLifestyleContent,
  type FeaturedBrandsSymphonyVideoContent,
  type FeaturedBrandsVideoContent,
} from '../types';
import { generatePublishProgramAsins } from './generatePublishProgramAsins';
import { getRemoteSearchOntology } from './getRemoteSearchOntology';
import { getStrategyName } from './getStrategyName';
import { getValidProgramAsins, hasProgramAsins } from './hasProgramAsins';

type GenerateContentArgs = {
  campaignName: string;
  flavor: Pick<FeaturedBrandsFlavor, 'creativeName' | 'hasGeneratedName' | 'content' | 'scheduling'>;
  sharedVarsMap: SharedVarsMap;
  assetIdToUrlMap: AssetIdToUrlMap;
  campaignId: string;
};

type GenerateCommonContentArgs = GenerateContentArgs & {
  creativeType: FEATURED_BRANDS_TEMPLATES;
  useFullAssetUrl?: boolean;
};

/**
 * Helper function to conditionally include authentication properties based on feature flag.
 * When using Ganon strategy, these properties are excluded.
 *
 * @param {boolean} useGanonStrategy - Whether Ganon strategy is enabled
 * @param {SymphonyRealm} realm - The Symphony realm for service URL
 * @returns {Object} Authentication properties or empty object
 */
const getAuthenticationProps = (useGanonStrategy: boolean, realm: SymphonyRealm): Record<string, string> =>
  useGanonStrategy
    ? {}
    : {
        aaaServiceName: AAA_SERVICE_NAME,
        authProtocol: AUTH_PROTOCOL,
        serviceUrl: SERVICE_URL[realm as keyof typeof SERVICE_URL],
      };

/**
 * Generates common properties for Featured Brands Symphony content.
 *
 * @param {Object} args - The arguments for generating the common properties.
 * @param {Object} args.flavor - The flavor object containing campaign details.
 * @param {Object} args.sharedVarsMap - A map of shared variables.
 * @param {Object} args.assetIdToUrlMap - A map of asset IDs to URLs.
 * @param {string} args.campaignName - The name of the campaign.
 * @param {string} args.campaignId - The ID of the campaign.
 * @param {string} args.creativeType - The type of creative (e.g., 'LIFESTYLE_PRODUCT_COLLECTION' or 'VIDEO').
 * @returns {FeaturedBrandsSymphonyCommonContent} The common properties for Featured Brands Symphony content.
 */
const generateCommonContent = ({
  flavor,
  sharedVarsMap,
  assetIdToUrlMap,
  campaignName,
  campaignId,
  creativeType,
  useFullAssetUrl = false,
}: GenerateCommonContentArgs): FeaturedBrandsSymphonyCommonContent => {
  const useGanonStrategy = isFeatureEnabled('FB_USE_GANON_STRATEGY_COMPONENT');
  const {
    hasGeneratedName,
    creativeName = '',
    content,
    scheduling: { language, marketplace, platform },
  } = flavor;

  let finalCreativeName = creativeName ?? '';
  if (hasGeneratedName) {
    finalCreativeName = generateFlavorCreativeName({
      campaignName,
      campaignSchema: TrafficCampaignSchema.FEATURED_BRANDS,
      language: language as Language,
      marketplace: marketplace as Marketplace,
      platform: platform as PLATFORMS,
    });
  }

  const brandName = getSharedOrOwnedValue({
    field: content.brandName,
    sharedVarsMap,
  });
  const brandNameForSlug = brandName
    .replace(/[^a-zA-Z ]/g, '')
    .replace(/ /g, '-')
    .toLowerCase();

  const strategyName = getStrategyName(creativeType, content, sharedVarsMap, brandNameForSlug, campaignId);

  const orgUnit = marketplaceToConfig[MARKETPLACE_TO_MARKETPLACE_AMAZON[marketplace as Marketplace]].orgUnit;

  const refTaggedLink = {
    autoGenerateRefTag: false,
    linkTypeAndLinkTypeValue: content.linkTypeAndLinkTypeValue,
    refTag: '',
  };
  const generatedDestinationUrl = generateDestinationUrlForFlavor({
    creativeName: finalCreativeName,
    flavorPlatform: platform as PLATFORMS,
    hasMultipleAreas: false,
    refTaggedLink,
    sharedVarsMap,
  });
  const currentMarketplace = `${flavor.scheduling.marketplace}Amazon` as MarketplaceAmazon;
  const marketplaceDomain = `${marketplaceToConfig[currentMarketplace].siteName}`;
  const destinationUrlLink = `https://${marketplaceDomain}${generatedDestinationUrl}`;

  const realm = MARKETPLACE_TO_REALM[flavor.scheduling.marketplace as Marketplace] as unknown as SymphonyRealm;

  const brandLogoAssetUrl = getAssetUrl('brandLogo' as keyof FeaturedBrandsContent, content, assetIdToUrlMap);
  const brandLogoRawUrl = Array.isArray(brandLogoAssetUrl) ? brandLogoAssetUrl[0] : brandLogoAssetUrl;
  const brandLogoUrl = useFullAssetUrl ? brandLogoRawUrl : shortenMediaCentralUrl(brandLogoRawUrl, orgUnit);

  const publishProgramAsins = JSON.stringify(generatePublishProgramAsins(getValidProgramAsins(content, sharedVarsMap)));

  const remoteSearchOntology = getRemoteSearchOntology(creativeType, content, sharedVarsMap);

  return {
    ...getAuthenticationProps(useGanonStrategy, realm),
    cti: CTI,
    name: strategyName,
    ...(hasProgramAsins(content, sharedVarsMap)
      ? {
          'remote.programAsins': publishProgramAsins,
        }
      : {}),
    'remote.brandLogoUrl': brandLogoUrl,
    'remote.brandName': brandName,
    'remote.creativeType': creativeType,
    'remote.headline': content.headline,
    'remote.landingPageUrl': destinationUrlLink,
    'remote.logWidgetContent': true,
    'remote.search.ontology': remoteSearchOntology,
    remoteStrategyContextKeys: REMOTE_STRATEGY_CONTEXT_KEYS,
    type: SERVICE_TYPE,
    widgetProcessingExclusions: WIDGET_PROCESSING_EXCLUSIONS,
  };
};

export const generateLifestyleContent = ({
  flavor,
  sharedVarsMap,
  assetIdToUrlMap,
  campaignName,
  campaignId,
}: GenerateContentArgs): FeaturedBrandsSymphonyLifestyleContent => {
  const useGanonStrategy = isFeatureEnabled('FB_USE_GANON_STRATEGY_COMPONENT');
  const {
    hasGeneratedName,
    creativeName = '',
    content,
    scheduling: { language, marketplace, platform },
  } = flavor;

  let finalCreativeName = creativeName ?? '';
  if (hasGeneratedName) {
    finalCreativeName = generateFlavorCreativeName({
      campaignName,
      campaignSchema: TrafficCampaignSchema.FEATURED_BRANDS,
      language: language as Language,
      marketplace: marketplace as Marketplace,
      platform: platform as PLATFORMS,
    });
  }

  const brandName = getSharedOrOwnedValue({
    field: content.brandName,
    sharedVarsMap,
  });
  const brandNameForRss = brandName
    .replace(/[^a-zA-Z ]/g, '')
    .replace(/ /g, '-')
    .toLowerCase();

  const rssIdPrefix = hasProgramAsins(content, sharedVarsMap) ? 'featured-brands-ASIN' : 'featured-brands';
  const rssIdName = `${rssIdPrefix}-${brandNameForRss}-${campaignId}`;

  const orgUnit = marketplaceToConfig[MARKETPLACE_TO_MARKETPLACE_AMAZON[marketplace as Marketplace]].orgUnit;

  const { brandLogoUrl, customImagePath } = findBrandLogoAndCustomImageMapAssets(content, assetIdToUrlMap);

  const refTaggedLink = {
    autoGenerateRefTag: false,
    linkTypeAndLinkTypeValue: content.linkTypeAndLinkTypeValue,
    refTag: '',
  };
  const generatedDestinationUrl = generateDestinationUrlForFlavor({
    creativeName: finalCreativeName,
    flavorPlatform: platform as PLATFORMS,
    hasMultipleAreas: false,
    refTaggedLink,
    sharedVarsMap,
  });
  const currentMarketplace = `${flavor.scheduling.marketplace}Amazon` as MarketplaceAmazon;
  const marketplaceDomain = `${marketplaceToConfig[currentMarketplace].siteName}`;
  const destinationUrlLink = `https://${marketplaceDomain}${generatedDestinationUrl}`;

  const realm = MARKETPLACE_TO_REALM[flavor.scheduling.marketplace as Marketplace] as unknown as SymphonyRealm;

  const publishProgramAsins = JSON.stringify(generatePublishProgramAsins(getValidProgramAsins(content, sharedVarsMap)));

  const newFeaturedBrandLifestyle: FeaturedBrandsSymphonyLifestyleContent = {
    ...getAuthenticationProps(useGanonStrategy, realm),
    cti: CTI,
    experiment: EXPERIMENT_WEBLAB,
    name: rssIdName,
    'remote.brandLogoUrl': shortenMediaCentralUrl(Array.isArray(brandLogoUrl) ? brandLogoUrl[0] : brandLogoUrl, orgUnit),
    'remote.brandName': brandName,
    'remote.creativeType': 'LIFESTYLE_PRODUCT_COLLECTION',
    'remote.customImageAltText': (content as FeaturedBrandsLifestyleContent).lifestyleImageAltText,
    'remote.customImagePath': shortenMediaCentralUrl(Array.isArray(customImagePath) ? customImagePath[0] : customImagePath, orgUnit),
    'remote.headline': content.headline,
    'remote.landingPageUrl': destinationUrlLink,
    'remote.logWidgetContent': true,
    'remote.search.ontology': hasProgramAsins(content, sharedVarsMap)
      ? REMOTE_SEARCH_ONTOLOGY_LIFESTYLE_ASIN
      : REMOTE_SEARCH_ONTOLOGY_LIFESTYLE,
    ...(hasProgramAsins(content, sharedVarsMap)
      ? {
          'remote.programAsins': publishProgramAsins,
        }
      : {}),
    remoteStrategyContextKeys: REMOTE_STRATEGY_CONTEXT_KEYS,
    type: SERVICE_TYPE,
    widgetProcessingExclusions: WIDGET_PROCESSING_EXCLUSIONS,
  };

  return newFeaturedBrandLifestyle;
};

/**
 * Generates video content for a Featured Brands campaign.
 *
 * @param {Object} args - The arguments for generating the video content.
 * @param {Object} args.flavor - The flavor object containing campaign details.
 * @param {Object} args.sharedVarsMap - A map of shared variables.
 * @param {Object} args.assetIdToUrlMap - A map of asset IDs to URLs.
 * @param {string} args.campaignName - The name of the campaign.
 * @param {string} args.campaignId - The ID of the campaign.
 * @returns {FeaturedBrandsSymphonyVideoContent} The generated video content.
 */
export const generateVideoContent = ({
  flavor,
  sharedVarsMap,
  assetIdToUrlMap,
  campaignName,
  campaignId,
}: GenerateContentArgs): FeaturedBrandsSymphonyVideoContent => {
  const {
    content,
    scheduling: { marketplace },
  } = flavor;

  const orgUnit: string = marketplaceToConfig[MARKETPLACE_TO_MARKETPLACE_AMAZON[marketplace as Marketplace]].orgUnit;

  const videoMediaPath =
    getMediaPath({
      assetIdToUrlMap,
      content,
      key: 'video' as keyof FeaturedBrandsContent,
      orgUnit,
    }) ?? '';

  const videoSlateImageMediaPath =
    getMediaPath({
      assetIdToUrlMap,
      content,
      key: 'slate' as keyof FeaturedBrandsContent,
      orgUnit,
    }) ?? '';

  const commonContent = generateCommonContent({
    assetIdToUrlMap,
    campaignId,
    campaignName,
    creativeType: FEATURED_BRANDS_TEMPLATES.VIDEO,
    flavor,
    sharedVarsMap,
  });

  const videoCaptionMediaPath: string = (content as FeaturedBrandsVideoContent).videoCaptionMediaPath ?? '';
  const videoAltText: string = (content as FeaturedBrandsVideoContent).videoAltText;

  const featuredBrandsSymphonyVideoContent: FeaturedBrandsSymphonyVideoContent = {
    ...commonContent,
    experiment: EXPERIMENT_WEBLAB,
    'remote.videoAltText': videoAltText,
    'remote.videoMediaPath': videoMediaPath,
    'remote.videoSlateImageMediaPath': videoSlateImageMediaPath,
  };

  if (videoCaptionMediaPath) {
    featuredBrandsSymphonyVideoContent['remote.videoCaptionMediaPath'] = videoCaptionMediaPath;
  }

  return featuredBrandsSymphonyVideoContent;
};

const findBrandLogoAndCustomImageMapAssets = (
  content: FeaturedBrandsContent,
  assetIdToUrlMap: AssetIdToUrlMap,
): {
  brandLogoUrl: string;
  customImagePath: string;
} => {
  const { brandLogo, lifestyleImage } = content as FeaturedBrandsLifestyleContent;
  if (!brandLogo) throw new Error('Expected a brandLogo on fb content');
  if (!lifestyleImage) throw new Error('Expected a lifestyleImage on fb content');
  const brandLogoContent = assetIdToUrlMap[brandLogo.assetId];
  const brandLogoPublished = brandLogoContent.publishedAssets?.find((pa) => pa.assetId === brandLogo.assetId);
  const lifestyleImageContent = assetIdToUrlMap[lifestyleImage.assetId];
  const lifestyleImagePublished = lifestyleImageContent.publishedAssets?.find((pa) => pa.assetId === lifestyleImage.assetId);

  if (!brandLogoPublished?.publicUrl) throw new Error('Missing brand logo asset URL');
  if (!lifestyleImagePublished?.publicUrl) throw new Error('Missing lifestyle image asset URL');

  return {
    brandLogoUrl: brandLogoPublished.publicUrl,
    customImagePath: lifestyleImagePublished.publicUrl,
  };
};

/**
 * Determines if the specified media path should be added to the content.
 *
 * @param {Object} args - An object containing the arguments.
 * @param {string} args.key - The key in the content object to check for.
 * @param {Object} args.content - The content object containing details about the campaign.
 * @param {string} args.orgUnit - The organization unit for the campaign.
 * @param {Object} args.assetIdToUrlMap - A map of asset IDs to URLs.
 * @returns {string | undefined} The specified media path or undefined if it should not be added.
 */
const getMediaPath = ({
  key,
  content,
  orgUnit,
  assetIdToUrlMap,
}: {
  key: keyof FeaturedBrandsContent;
  content: FeaturedBrandsContent;
  orgUnit: string;
  assetIdToUrlMap: AssetIdToUrlMap;
}): string | undefined => {
  const keyValue = get(content, key);
  if (!isNil(keyValue)) {
    const assetUrl: string | string[] = getAssetUrl(key as keyof FeaturedBrandsContent, content, assetIdToUrlMap);
    const mediaPath: string = shortenMediaCentralUrl(Array.isArray(assetUrl) ? assetUrl[0] : assetUrl, orgUnit);

    if (!isNil(mediaPath) && !isEmpty(mediaPath)) {
      return mediaPath;
    }
  }

  return;
};

/**
 * Finds the public URL for a specific asset.
 *
 * @param fieldName - The name of the field containing the asset model.
 * @param content - An object containing properties with asset IDs.
 * @param assetIdToContentMap - An object mapping asset IDs to their corresponding asset content.
 * @returns The public URL of the asset, or throws an error if not found.
 */
const getAssetUrl = (
  fieldName: keyof FeaturedBrandsContent,
  content: FeaturedBrandsContent,
  assetIdToContentMap: AssetIdToUrlMap,
): string => {
  const propertyValue = content[fieldName];

  // Check if fieldName is a valid key in FeaturedBrandsContent
  if (!propertyValue || !(propertyValue as MediaAssetModel).assetId) {
    throw new Error(`Invalid field "${fieldName}" in FeaturedBrandsContent`);
  }

  const assetId = (propertyValue as MediaAssetModel).assetId;

  // Check if asset content is found in assetIdToContentMap
  if (!assetId || !assetIdToContentMap[assetId]) {
    throw new Error(`Missing asset content for asset ID "${assetId}"`);
  }

  const assetContent = assetIdToContentMap[assetId];
  const publishedAsset = assetContent.publishedAssets?.find((pa) => pa.assetId === assetId);

  // Check if publishedAsset exists and has a publicUrl
  if (!publishedAsset || !publishedAsset.publicUrl) {
    throw new Error(`Missing public URL for asset ID "${assetId}"`);
  }

  return publishedAsset.publicUrl;
};

/**
 * Generates brands collections content for a Featured Brands campaign.
 * This template is similar to lifestyle but without the lifestyle image fields.
 * BRANDS_COLLECTIONS always requires ASINs.
 *
 * @param {Object} args - The arguments for generating the brands collections content.
 * @param {Object} args.flavor - The flavor object containing campaign details.
 * @param {Object} args.sharedVarsMap - A map of shared variables.
 * @param {Object} args.assetIdToUrlMap - A map of asset IDs to URLs.
 * @param {string} args.campaignName - The name of the campaign.
 * @param {string} args.campaignId - The ID of the campaign.
 * @returns {FeaturedBrandsSymphonyBrandsCollectionsContent} The generated brands collections content.
 * @throws {Error} If programAsins are not provided for BRANDS_COLLECTIONS template
 */
export const generateBrandsCollectionsContent = ({
  flavor,
  sharedVarsMap,
  assetIdToUrlMap,
  campaignName,
  campaignId,
}: GenerateContentArgs): FeaturedBrandsSymphonyBrandsCollectionsContent => {
  const { content } = flavor;

  // BRANDS_COLLECTIONS requires ASINs
  if (!hasProgramAsins(content, sharedVarsMap)) {
    throw new Error('BRANDS_COLLECTIONS template requires programAsins');
  }

  const commonContent = generateCommonContent({
    assetIdToUrlMap,
    campaignId,
    campaignName,
    creativeType: FEATURED_BRANDS_TEMPLATES.BRANDS_COLLECTIONS,
    flavor,
    sharedVarsMap,
    useFullAssetUrl: true,
  });

  const featuredBrandsSymphonyBrandsCollectionsContent: FeaturedBrandsSymphonyBrandsCollectionsContent = {
    ...commonContent,
    experiment: EXPERIMENT_WEBLAB,
  };

  return featuredBrandsSymphonyBrandsCollectionsContent;
};

/**
 * A mapping between Featured Brands templates and their corresponding Symphony content generation functions.
 *
 * @type {Record<FEATURED_BRANDS_TEMPLATES, (args: GenerateContentArgs) => FeaturedBrandsSymphonyContent>}
 */
export const templateToSymphonyContentMap: Record<FEATURED_BRANDS_TEMPLATES, (args: GenerateContentArgs) => FeaturedBrandsSymphonyContent> =
  {
    [FEATURED_BRANDS_TEMPLATES.BRANDS_COLLECTIONS]: generateBrandsCollectionsContent,
    [FEATURED_BRANDS_TEMPLATES.LIFESTYLE_PRODUCT_COLLECTION]: generateLifestyleContent,
    [FEATURED_BRANDS_TEMPLATES.VIDEO]: generateVideoContent,
  } as const;
